import React, { FC } from "react";
import { useDialogShadow, IconTypesKeys } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { getGeometryType } from "../../../../../evergis/core/attributes/geometryTypes";
import { formatDate } from "../../../../../utils/date";
import { formatNumber } from "../../../../../utils/formatNumber";
import { LinearProgress, LoadingDescription } from "../../../BulkCopy/styled";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { DialogTitle } from "../../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../../DialogComponents/FullScreenDialog";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";

import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { BlackIcon } from "../../../EditResource/components/Table/styled";
import { DataField, FieldValue, styledHeaderText } from "../../../styled";
import { useCopyTableJob } from "./hooks/useCopyTableJob";

export const TableCopy: FC = () => {
  const { t } = useTranslation();
  const { table, meta, updateMeta, loading, onApply, waiting } = useCopyTableJob();
  const { shadows, setScrollContainer } = useDialogShadow();
  const { changedDate, objectsCount, geometryType } = table;

  return (
    <FullScreenDialog
      dialogContext={{
        title: {
          text: styledHeaderText(t("copyResource.copyTable", { ns: "dialogs" })),
          icon: "copy",
        },
        meta: {
          aliasLabel: t("copyResource.tableName", { ns: "dialogs" }),
          descriptionLabel: t("copyResource.tableDescription", { ns: "dialogs" }),
          loaderLabel: t("copyResource.uploadTablePreview", { ns: "dialogs" }),
        },
        applyText: t("actions.copy", { ns: "common" }),
      }}
    >
      <DialogTitle displayShadow={shadows.upper} />
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          {loading ? (
            <>
              <LoadingDescription>{t("copyResource.gettingTableData", { ns: "dialogs" })}</LoadingDescription>
              <LinearProgress />
            </>
          ) : (
            <>
              <ProjectMetaBlock>
                <ResourceMeta meta={meta} updateMeta={updateMeta}>
                  <DataField column>
                    <FieldValue field={t("copyResource.updated", { ns: "dialogs" })} value={formatDate(changedDate)} />
                    <DataField>
                      <FieldValue
                        field={t("copyResource.geometryType", { ns: "dialogs" })}
                        value={
                          <>
                            {geometryType && (
                              <BlackIcon
                                kind={`geom_${geometryType}` as IconTypesKeys}
                                style={{ marginRight: "0.5rem" }}
                              />
                            )}
                            <span>{t(getGeometryType(geometryType), { ns: "common" })}</span>
                          </>
                        }
                      />
                      <FieldValue
                        field={t("copyResource.totalFeatures", { ns: "dialogs" })}
                        value={formatNumber(objectsCount)}
                      />
                    </DataField>
                  </DataField>
                </ResourceMeta>
              </ProjectMetaBlock>
            </>
          )}
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyDisabled={!meta.alias || loading}
        onApply={onApply}
        isWaiting={waiting}
      />
    </FullScreenDialog>
  );
};
